import { FILTER_NAME_PATTERN, FILTER_VALUE_PATTERN_V2 } from './filter-util';
import { invariant } from '../util/invariant';

/** A half-open character range: [start, end). start is included, end is not, like
 *  string.slice(start, end). In 'person:obama', { start: 0, end: 6 } is 'person'. */
export interface TextRange {
  start: number;
  end: number;
}

/**
 * A field:value filter such as person:obama or title:"opening ceremony". field is the text
 * before the colon, value the text after it with any wrapping quotes stripped. quote says
 * which quote style wrapped the value, or null when it was unquoted. A quoted value counts as
 * an exact search (isExactSearch true). isSticky records a trailing |sticky marker such as
 * transcript:"climate summit"|sticky.
 */
export interface FilterToken {
  type: 'filter';
  position: TextRange;
  text: string;
  field: string;
  value: string;
  quote: '"' | "'" | null;
  isExactSearch: boolean;
  isSticky: boolean;
}

/**
 * A boolean operator joining two operands. operator is the meaning, normalised to AND or OR.
 * text is the exact spelling the user typed, kept separately because AND can be written AND,
 * && or +, and OR as OR, || or |. We keep both so callers can act on the meaning but still
 * rebuild the original string character for character.
 */
export interface OperatorToken {
  type: 'operator';
  position: TextRange;
  text: string;
  operator: 'AND' | 'OR';
}

/**
 * A standalone quoted phrase searched as exact text, such as "opening ceremony". It is a
 * phrase and not a filter because there is no field:value colon outside the quotes. value is
 * the inner text with the wrapping quotes removed. quote says which quote style was used.
 */
export interface PhraseToken {
  type: 'phrase';
  position: TextRange;
  text: string;
  value: string;
  quote: '"' | "'";
}

/** A bare word, no field and no quoting. The word sunset in "sunset person:obama" is one. */
export interface WordToken {
  type: 'word';
  position: TextRange;
  text: string;
  value: string;
}

/** Any single token the search string breaks into, discriminated by its type field. */
export type SearchToken = FilterToken | OperatorToken | PhraseToken | WordToken;

const STICKY_SUFFIX = '|sticky';
const AND_OPERATORS: ReadonlySet<string> = new Set(['AND', '&&', '+']);

/** Each rule is ^-anchored and matched against the slice at the current position, so no regex
 *  carries a mutable lastIndex and tokenizeSearchString stays re-entrant.
 *
 *  A quoted "field:value" (e.g. "type:file") is exact text, not a filter. Goes before the
 *  filter rule so its inner colon isn't read as one. */
const EXACT_SEARCH_RE = new RegExp(`^["'] *(?:${FILTER_NAME_PATTERN}) *: *(?:${FILTER_VALUE_PATTERN_V2}) *["']`);
const FILTER_RE = new RegExp(`^(?<field>${FILTER_NAME_PATTERN}) *: *(?<value>${FILTER_VALUE_PATTERN_V2})`);
const PHRASE_RE = /^(?:"[^"]*"|'[^']*')/;
/** The lookahead keeps +/| and the word operators from matching inside a word, so a+b and
 *  ANDROID stay words. */
const OPERATOR_RE = /^(?:AND|OR|&&|\|\||\+|\|)(?=\s|$)/;
const WORD_RE = /^\S+/;

/** Reads the search string left to right into a flat list of tokens, each carrying the
 *  span of text it came from. Every position yields exactly one token, so the spans tile
 *  the whole string, covering it end to end with no gaps or overlap.
 *  'person:obama OR sunset' -> [filter person:obama] [operator OR] [word sunset]. */
export function tokenizeSearchString(input: string): SearchToken[] {
  const tokens: SearchToken[] = [];
  let position = skipWhitespace(input, 0);
  while (position < input.length) {
    const token = nextToken(input, position);
    invariant(token.position.end > position, 'every token must advance past its start');
    tokens.push(token);
    position = skipWhitespace(input, token.position.end);
  }
  return tokens;
}

/**
 * The token rules in priority order. nextToken tries each one and takes the first match,
 * falling back to a word, so any position always yields a token.
 */
const TOKEN_RULES: ReadonlyArray<(rest: string, position: number) => SearchToken | null> = [
  matchExactSearchPhrase,
  matchFilter,
  matchPhrase,
  matchOperator,
];

function nextToken(input: string, position: number): SearchToken {
  // The ^-anchored rules match at the head of this slice.
  const rest = input.slice(position);
  for (const rule of TOKEN_RULES) {
    const token = rule(rest, position);
    if (token) {
      return token;
    }
  }
  return matchWord(rest, position);
}

export function getFilterTokens(tokens: SearchToken[]): FilterToken[] {
  return tokens.filter((token): token is FilterToken => token.type === 'filter');
}

/** Cuts highest-start-first so removing a later range never shifts an earlier one's offsets.
 *  removeRanges('abc def ghi', [{ start: 0, end: 3 }, { start: 8, end: 11 }]) -> 'def'. */
export function removeRanges(text: string, ranges: TextRange[]): string {
  const highestStartFirst = [...ranges].sort((a, b) => b.start - a.start);
  let remaining = text;
  for (const range of highestStartFirst) {
    remaining = remaining.slice(0, range.start) + remaining.slice(range.end);
  }
  return remaining.trim().replace(/\s+/g, ' ');
}

/** The ranges to cut when the given filters are pulled out: each consumed filter's span, plus any
 *  operator beside one. An orphaned 'OR'/'AND' left behind would build an empty query, so cut it too.
 *  Used by both the search box and the backend, so their residual matches. */
export function consumedFilterRanges(tokens: SearchToken[], consumed: ReadonlySet<SearchToken>): TextRange[] {
  const ranges: TextRange[] = [];
  for (const [index, token] of tokens.entries()) {
    if (consumed.has(token)) {
      ranges.push(token.position);
    } else if (token.type === 'operator' && touchesConsumed(tokens, index, consumed)) {
      ranges.push(token.position);
    }
  }
  return ranges;
}

/** Whether the token just before or just after this one is a consumed filter. */
function touchesConsumed(tokens: SearchToken[], index: number, consumed: ReadonlySet<SearchToken>): boolean {
  const before = tokens[index - 1];
  const after = tokens[index + 1];
  return (before !== undefined && consumed.has(before)) || (after !== undefined && consumed.has(after));
}

/**
 * The operator joining the tokens at leftIndex and rightIndex, or null if none. Only an operator in
 * an otherwise operator-only gap counts: a non-operator token between the two means they are not
 * adjacent operands, so its operator joins a different pair. This stops keywords:a OR person:x
 * keywords:b from reading the OR onto the keywords pair.
 */
export function operatorBetween(tokens: SearchToken[], leftIndex: number, rightIndex: number): 'AND' | 'OR' | null {
  let operator: 'AND' | 'OR' | null = null;
  for (const token of tokens.slice(leftIndex + 1, rightIndex)) {
    if (token.type !== 'operator') {
      return null;
    }
    if (operator === null) {
      operator = token.operator;
    }
  }
  return operator;
}

/** Turns a filter token back into its field:value text, keeping the quotes and sticky.
 *  the token parsed from transcript:"climate summit"|sticky rebuilds to exactly that string. */
export function filterTokenToText(token: FilterToken): string {
  const sticky = token.isSticky ? STICKY_SUFFIX : '';
  if (token.quote === null) {
    return `${token.field}:${token.value}${sticky}`;
  }
  return `${token.field}:${token.quote}${token.value}${token.quote}${sticky}`;
}

function skipWhitespace(input: string, from: number): number {
  let position = from;
  while (position < input.length && /\s/.test(input.charAt(position))) {
    position++;
  }
  return position;
}

function matchExactSearchPhrase(rest: string, position: number): PhraseToken | null {
  const text = matchAt(EXACT_SEARCH_RE, rest);
  return text === null ? null : phraseToken(text, position);
}

function matchFilter(rest: string, position: number): FilterToken | null {
  const match = FILTER_RE.exec(rest);
  if (!match) {
    return null;
  }
  const field = match.groups?.field;
  const rawValue = match.groups?.value;
  invariant(field !== undefined && rawValue !== undefined, 'a matched filter has a field and a value');
  return {
    type: 'filter',
    position: { start: position, end: position + match[0].length },
    text: match[0],
    field,
    ...parseFilterValue(rawValue),
  };
}

function matchPhrase(rest: string, position: number): PhraseToken | null {
  const text = matchAt(PHRASE_RE, rest);
  return text === null ? null : phraseToken(text, position);
}

function phraseToken(text: string, position: number): PhraseToken {
  return {
    type: 'phrase',
    position: { start: position, end: position + text.length },
    text,
    value: text.slice(1, -1),
    quote: text.charAt(0) === '"' ? '"' : "'",
  };
}

function matchOperator(rest: string, position: number): OperatorToken | null {
  const text = matchAt(OPERATOR_RE, rest);
  if (text === null) {
    return null;
  }
  return {
    type: 'operator',
    position: { start: position, end: position + text.length },
    text,
    operator: AND_OPERATORS.has(text) ? 'AND' : 'OR',
  };
}

function matchWord(rest: string, position: number): WordToken {
  const text = matchAt(WORD_RE, rest);
  invariant(text !== null, 'a non-space character always starts a word');
  return {
    type: 'word',
    position: { start: position, end: position + text.length },
    text,
    value: text,
  };
}

/** Matches an ^-anchored rule at the head of rest, returning the matched text or null. */
function matchAt(anchored: RegExp, rest: string): string | null {
  const match = anchored.exec(rest);
  return match ? match[0] : null;
}

/**
 * The value fields of a filter token. Only a quoted value is an exact search, but either kind can
 * be sticky. So obama lands on the unquoted arm (isExactSearch false) and "red carpet" on the
 * quoted arm (isExactSearch true), and both carry isSticky.
 */
type FilterValue =
  | { quote: null; value: string; isExactSearch: false; isSticky: boolean }
  | { quote: '"' | "'"; value: string; isExactSearch: true; isSticky: boolean };

/**
 * Reads the value fields out of a matched filter value. A trailing |sticky is the sticky marker on
 * a quoted or unquoted value, stripped only when a value precedes the pipe, matching
 * getExtraInfoFromFilterValue in the old reader.
 * e.g. obama|sticky -> value 'obama', isSticky true; a bare |sticky with nothing before the pipe
 * stays literal (value '|sticky', isSticky false).
 */
function parseFilterValue(rawValue: string): FilterValue {
  const isSticky = rawValue.length > STICKY_SUFFIX.length && rawValue.endsWith(STICKY_SUFFIX);
  const value = isSticky ? rawValue.slice(0, -STICKY_SUFFIX.length) : rawValue;
  const quote = value.charAt(0);
  if (quote !== '"' && quote !== "'") {
    return { quote: null, value, isExactSearch: false, isSticky };
  }
  const inner = value.slice(1, -1);
  // A double-quoted value can carry escaped characters, \" and \\, so unescape them back to the
  // literal value. Single-quoted values have no escapes and pass through unchanged.
  const unescaped = quote === '"' ? inner.replace(/\\(.)/g, '$1') : inner;
  return { quote, value: unescaped, isExactSearch: true, isSticky };
}
